import { useNode } from "@craftjs/core";
import React, { useState } from "react";
import {
  RangeInput,
  SelectInput,
  UnitInput,
  PaddingInput,
  MarginInput,
  Panel,
  Tabs,
  Notice,
  TextInput,
  Spacer,
  AlignInput,
  RichTextFormatting,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

export const GoogleMapSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    location = "New York",
    latitude = "40.7128",
    longitude = "-74.0060",
    zoom = "14",
    mapType = "roadmap",
    mapMode = "place",
    apiKey,
    align,
    alignTablet,
    alignMobile,
    height = "500px",
    heightTablet,
    heightMobile,
    maxWidth,
    maxWidthTablet,
    maxWidthMobile,
    language = "en",
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
  } = props;

  const LANGUAGE = [
    {
      label: "English",
      value: "en",
    },
    {
      label: "Afrikaans",
      value: "af",
    },
    {
      label: "Albanian",
      value: "sq",
    },
    {
      label: "Amharic",
      value: "am",
    },
    {
      label: "Arabic",
      value: "ar",
    },
    {
      label: "Armenian",
      value: "hy",
    },
    {
      label: "Azerbaijani",
      value: "az",
    },
    {
      label: "Basque",
      value: "eu",
    },
    {
      label: "Belarusian",
      value: "be",
    },
    {
      label: "Bengali",
      value: "bn",
    },
    {
      label: "Bosnian",
      value: "bs",
    },
    {
      label: "Bulgarian",
      value: "bg",
    },
    {
      label: "Catalan",
      value: "ca",
    },
    {
      label: "Chinese",
      value: "zh",
    },
    {
      label: "Chinese (Hong Kong)",
      value: "zh-HK",
    },
    {
      label: "Croatian",
      value: "hr",
    },
    {
      label: "Czech",
      value: "cs",
    },
    {
      label: "Danish",
      value: "da",
    },
    {
      label: "Dutch",
      value: "nl",
    },
    {
      label: "English (Australian)",
      value: "en-AU",
    },
    {
      label: "English (Great Britain)",
      value: "en-GB",
    },
    {
      label: "Estonian",
      value: "et",
    },
    {
      label: "Farsi",
      value: "fa",
    },
    {
      label: "Finnish",
      value: "fi",
    },
    {
      label: "Filipino",
      value: "fil",
    },
    {
      label: "French",
      value: "fr",
    },
    {
      label: "French (Canadian)",
      value: "fr-CA",
    },
    {
      label: "Galician",
      value: "gl",
    },
    {
      label: "Georgian",
      value: "ka",
    },
    {
      label: "German",
      value: "de",
    },
    {
      label: "Greek",
      value: "el",
    },
    {
      label: "Gujarati",
      value: "gu",
    },
    {
      label: "Hebrew",
      value: "iw",
    },
    {
      label: "Hindi",
      value: "hi",
    },
    {
      label: "Hungarian",
      value: "hu",
    },
    {
      label: "Icelandic",
      value: "is",
    },
    {
      label: "Indonesian",
      value: "id",
    },
    {
      label: "Italian",
      value: "it",
    },
    {
      label: "Japanese",
      value: "ja",
    },
    {
      label: "Kannada",
      value: "kn",
    },
    {
      label: "Kazakh",
      value: "kk",
    },
    {
      label: "Khmer",
      value: "km",
    },
    {
      label: "Korean",
      value: "ko",
    },
    {
      label: "Kyrgyz",
      value: "ky",
    },
    {
      label: "Lao",
      value: "lo",
    },
    {
      label: "Latvian",
      value: "lv",
    },
    {
      label: "Lithuanian",
      value: "lt",
    },
    {
      label: "Macedonian",
      value: "mk",
    },
    {
      label: "Malay",
      value: "ms",
    },
    {
      label: "Malayalam",
      value: "ml",
    },
    {
      label: "Marathi",
      value: "mr",
    },
    {
      label: "Mongolian",
      value: "mn",
    },
    {
      label: "Nepali",
      value: "ne",
    },
    {
      label: "Norwegian",
      value: "no",
    },
    {
      label: "Polish",
      value: "pl",
    },
    {
      label: "Portuguese",
      value: "pt",
    },
    {
      label: "Portuguese (Brazil)",
      value: "pt-BR",
    },
    {
      label: "Portuguese (Portugal)",
      value: "pt-PT",
    },
    {
      label: "Romanian",
      value: "ro",
    },
    {
      label: "Russian",
      value: "ru",
    },
    {
      label: "Serbian",
      value: "sr",
    },
    {
      label: "Sinhalese",
      value: "si",
    },
    {
      label: "Slovak",
      value: "sk",
    },
    {
      label: "Slovenian",
      value: "sl",
    },
    {
      label: "Spanish",
      value: "es",
    },
    {
      label: "Spanish (Latin America)",
      value: "es-419",
    },
    {
      label: "Swahili",
      value: "sw",
    },
    {
      label: "Swedish",
      value: "sv",
    },
    {
      label: "Tamil",
      value: "ta",
    },
    {
      label: "Telugu",
      value: "te",
    },
    {
      label: "Thai",
      value: "th",
    },
    {
      label: "Turkish",
      value: "tr",
    },
    {
      label: "Ukrainian",
      value: "uk",
    },
    {
      label: "Urdu",
      value: "ur",
    },
    {
      label: "Uzbek",
      value: "uz",
    },
    {
      label: "Vietnamese",
      value: "vi",
    },
    {
      label: "Zulu",
      value: "zu",
    },
  ];

  const MAPTYPE = [
    {
      label: "Roadmap",
      value: "roadmap",
    },
    {
      label: "Satellite",
      value: "satellite",
    },
  ];

  const MAPMODE = [
    {
      label: "Place",
      value: "place",
    },
    {
      label: "View",
      value: "view",
    },
  ];

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            resetValue={"place"}
            value={mapMode}
            onChange={(val) => {
              setProp((props) => {
                props.mapMode = val;
              });
            }}
            options={MAPMODE.map(function (item) {
              return {
                label: item.label,
                value: item.value,
              };
            })}
            label="Map Mode"
          />
          {mapMode === "place" ? (
            <TextInput
              minLength={INPUT_LIMITS["google-map-location"].minlength}
              maxLength={INPUT_LIMITS["google-map-location"].maxlength}
              resetValue={"New York"}
              label={"Location"}
              value={location}
              onChange={(val) => {
                setProp((props) => {
                  props.location = val;
                });
              }}
            />
          ) : (
            <>
              <TextInput
                minLength={INPUT_LIMITS["google-map-latitude"].minlength}
                maxLength={INPUT_LIMITS["google-map-latitude"].maxlength}
                resetValue={"40.7128"}
                label={"Latitude"}
                value={latitude}
                onChange={(val) => {
                  setProp((props) => {
                    props.latitude = val;
                  });
                }}
              />
              <TextInput
                minLength={INPUT_LIMITS["google-map-longitude"].minlength}
                maxLength={INPUT_LIMITS["google-map-longitude"].maxlength}
                resetValue={"-74.0060"}
                label={"Longitude"}
                value={longitude}
                onChange={(val) => {
                  setProp((props) => {
                    props.longitude = val;
                  });
                }}
              />
            </>
          )}

          <RangeInput
            resetValue={"14"}
            value={zoom}
            onChange={(val) => {
              setProp((props) => {
                props.zoom = val;
              });
            }}
            // min={1}
            // max={20}
            min={INPUT_LIMITS["cms-google-map-zoom"].min}
            max={INPUT_LIMITS["cms-google-map-zoom"].max}
            label={"Zoom"}
          />
          <SelectInput
            resetValue={"roadmap"}
            value={mapType}
            onChange={(val) => {
              setProp((props) => {
                props.mapType = val;
              });
            }}
            options={MAPTYPE}
            label="Map Type"
          />
          <SelectInput
            resetValue={"en"}
            value={language}
            onChange={(val) => {
              setProp((props) => {
                props.language = val;
              });
            }}
            options={LANGUAGE}
            label="Language"
          />
          <AlignInput
            resetValue={undefined}
            value={align}
            isResponsive={true}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: apiKey }])}
          id={`block-setting-${id}-custom-api-key`}
          title={"Custom API Key"}
          initialOpen={false}
        >
          <Notice
            text="Google Maps Block uses Grigora's API key and is managed regularly by the admins. You don't need to add any API key to make this map work. If you still want to change the key to your API key, you can edit the field below.
"
            status="success"
          />
          <TextInput
            minLength={INPUT_LIMITS["google-map-api-key"].minlength}
            maxLength={INPUT_LIMITS["google-map-api-key"].maxlength}
            resetValue={undefined}
            label={"API key"}
            value={apiKey}
            onChange={(val) => {
              setProp((props) => {
                props.apiKey = val;
              });
            }}
          />
        </Panel>
        <RichTextFormatting />
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Spacer spacing="1rem">
            <UnitInput
              resetValue={"500px"}
              value={height}
              isResponsive={true}
              valueTablet={heightTablet}
              valueMobile={heightMobile}
              onChange={(val) => {
                setProp((props) => {
                  props.height = val;
                });
              }}
              setValueTablet={(val) => {
                setProp((props) => {
                  props.heightTablet = val;
                });
              }}
              setValueMobile={(val) => {
                setProp((props) => {
                  props.heightMobile = val;
                });
              }}
              label={"Height"}
            />
            <UnitInput
              isResponsive={true}
              valueTablet={maxWidthTablet}
              valueMobile={maxWidthMobile}
              resetValue={undefined}
              value={maxWidth}
              onChange={(val) => {
                setProp((props) => {
                  props.maxWidth = val;
                });
              }}
              setValueTablet={(val) => {
                setProp((props) => {
                  props.maxWidthTablet = val;
                });
              }}
              setValueMobile={(val) => {
                setProp((props) => {
                  props.maxWidthMobile = val;
                });
              }}
              label={"Max Width"}
            />
          </Spacer>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: layoutMarginTablet },
              { key: layoutMarginMobile },
              { key: layoutMargin },
              { key: layoutPadding },
              { key: layoutPaddingTablet },
              { key: layoutPaddingMobile },
            ])}
            id={`block-setting-${id}-layout`}
            title={"Layout"}
            initialOpen={true}
          >
            <MarginInput
              isResponsive={true}
              valueTablet={layoutMarginTablet}
              valueMobile={layoutMarginMobile}
              resetValue={undefined}
              value={layoutMargin}
              onChange={(val) => {
                setProp((props) => {
                  props.layoutMargin = val;
                });
              }}
              setValueTablet={(val) => {
                setProp((props) => {
                  props.layoutMarginTablet = val;
                });
              }}
              setValueMobile={(val) => {
                setProp((props) => {
                  props.layoutMarginMobile = val;
                });
              }}
              label={"Margin"}
            />
            <PaddingInput
              isResponsive={true}
              resetValue={undefined}
              value={layoutPadding}
              valueTablet={layoutPaddingTablet}
              valueMobile={layoutPaddingMobile}
              onChange={(val) => {
                setProp((props) => {
                  props.layoutPadding = val;
                });
              }}
              setValueTablet={(val) => {
                setProp((props) => {
                  props.layoutPaddingTablet = val;
                });
              }}
              setValueMobile={(val) => {
                setProp((props) => {
                  props.layoutPaddingMobile = val;
                });
              }}
              label={"Padding"}
            />
          </Panel>
        </div>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: mapMode },
        { key: location },
        { key: latitude },
        { key: longitude },
        { key: zoom },
        { key: mapType },
        { key: language },
        { key: alignTablet },
        { key: alignMobile },
        { key: align },
        { key: apiKey },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: height },
        { key: heightTablet },
        { key: heightMobile },
        { key: maxWidthTablet },
        { key: maxWidthMobile },
        { key: maxWidth },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutMargin },
        { key: layoutPadding },
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
